import { Button } from '@today/ui-primitives'
import { type ReactElement, type ReactNode, useId } from 'react'

/** Props of {@link SettingsSection}. */
interface SettingsSectionProps {
	title: string
	description: string
	/** A server refusal, announced as an alert under the header. */
	error?: string | null
	children: ReactNode
}

/** The shared frame of a Settings section: a region named by its h2, with an optional alert (TO-197). */
export function SettingsSection(props: SettingsSectionProps): ReactElement {
	const titleId = useId()
	return (
		<section
			aria-labelledby={titleId}
			className="flex flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6"
		>
			<header className="flex flex-col gap-1">
				<h2 id={titleId} className="text-ds-lg font-semibold text-ds-text">
					{props.title}
				</h2>
				<p className="text-ds-sm text-ds-text-muted">{props.description}</p>
			</header>
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.children}
		</section>
	)
}

/** Props of {@link SaveRow}. */
interface SaveRowProps {
	label: string
	savedLabel: string
	onSave: () => void
	isSaving?: boolean
	saved?: boolean
}

/** A section's save action and its "saved" status (TO-197). */
export function SaveRow(props: SaveRowProps): ReactElement {
	return (
		<div className="flex items-center gap-3">
			<Button onPress={props.onSave} isDisabled={props.isSaving}>
				{props.label}
			</Button>
			{props.saved ? (
				<p role="status" className="text-ds-sm text-ds-text-muted">
					{props.savedLabel}
				</p>
			) : null}
		</div>
	)
}
